import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import { getIn } from 'formik';
import type { AnyAction, Dispatch } from 'redux';

import { toggleUseTo } from '../../utils/addressHelpers';
import { states } from '../../utils/constants';
import ToggleButton from '../ToggleButton';
import type { AddressData, ApiServiceLike, FormikLike, StoreLike } from './types';

interface ToggleAddressProps {
  /** Name of address property in the store. */
  name: string;
  /** Redux store slice containing the customer. */
  store: StoreLike;
  /** Triggers reducers and updates the store based on action. */
  dispatch: Dispatch<AnyAction>;
  /** Formik object to handle form validation and submission. */
  formik?: FormikLike;
  /** Service used to persist customer changes. */
  apiService?: ApiServiceLike;
  /** Renders billTo toggle option when true. */
  showBillTo?: boolean;
  /** Renders payer toggle option when true. */
  showPayer?: boolean;
}

interface SameAsArgs {
  formik?: FormikLike;
  useSoldTo: boolean;
  useBillTo: boolean;
  usePayer: boolean;
  address?: AddressData;
}

function ToggleAddress({ formik, store, name, dispatch, showBillTo, showPayer, apiService }: ToggleAddressProps) {
  const { t } = useTranslation();
  const handleOnSave = () => {
    // SetTimeout acts as a tick to get the updated value since the toggle calculation is expensive
    setTimeout(() => apiService?.updateCustomer(store.customer, dispatch));
  };

  // Get useSoldTo, useBillTo, usePayer from the address in the store
  const useSoldTo = getIn(store.customer, `${name}.useSoldTo`);
  const useBillTo = getIn(store.customer, `${name}.useBillTo`);
  const usePayer = getIn(store.customer, `${name}.usePayer`);

  // Varaible use to toggle AddressBuilder and show toggle button as active when all properties are false
  const isDifferent = !useBillTo && !useSoldTo && !usePayer;

  // Handle event for same as/different address toggle
  const handleSameAs = async ({ formik: activeFormik, useSoldTo, useBillTo, usePayer }: SameAsArgs) => {
    if (!activeFormik) {
      return;
    }
    // Updates useSoldTo, useBillTo and usePayer fields
    await toggleUseTo(dispatch, activeFormik, name, useSoldTo, useBillTo, usePayer);
    // This event will trigger update request to the api with new value
    handleOnSave();
  };

  const { soldTo = {}, billTo = {}, payer = {}, region = '', countryCode = '' } = store.customer;
  const soldToAddress = soldTo.address;
  const billToAddress = billTo.address;
  const payerAddress = payer.address;

  const getStateLable = (stateCode?: string) => {
    const stateObject = states.find((element: { value: string; label: string }) => element.value === stateCode);
    return stateObject ? stateObject.label : stateCode;
  };

  const getAddressString = (address: AddressData = {}) =>
    region.toLowerCase() === 'latam' && countryCode.toLowerCase() !== 'mx'
      ? `${address.street || '__'} ${address.city || '__'}, ${
          getStateLable(address.state) || '__'
        }, ${address.district || '__'}`
      : `${address.street || '__'} ${address.city || '__'}, ${getStateLable(address.state) || '__'}`;

  const getSubLabel = (address: AddressData = {}) => {
    const { useBillTo, useSoldTo } = address;
    if (useBillTo) {
      return getAddressString(billToAddress);
    }
    if (useSoldTo) {
      return getAddressString(soldToAddress);
    }
    return getAddressString(address);
  };

  return (
    <Box>
      <ToggleButton
        onClick={() => {
          !useSoldTo &&
            handleSameAs({
              formik,
              useSoldTo: true,
              useBillTo: false,
              usePayer: false,
              address: soldToAddress
            });
        }}
        isActive={useSoldTo}
        label={t('sameAsAddress.soldTo')}
        subLabel={getSubLabel(soldToAddress)}
      />
      {showBillTo && (
        <ToggleButton
          onClick={() => {
            !useBillTo &&
              handleSameAs({
                formik,
                useSoldTo: false,
                useBillTo: true,
                usePayer: false,
                address: billToAddress
              });
          }}
          isActive={useBillTo}
          label={t('sameAsAddress.billTo')}
          subLabel={getSubLabel(billToAddress)}
        />
      )}
      {showPayer && (
        <ToggleButton
          onClick={() => {
            !usePayer &&
              handleSameAs({
                formik,
                useSoldTo: false,
                useBillTo: false,
                usePayer: true,
                address: payerAddress
              });
          }}
          isActive={usePayer}
          label={t('sameAsAddress.payer')}
          subLabel={getSubLabel(payerAddress)}
        />
      )}
      <ToggleButton
        onClick={() => {
          !isDifferent &&
            handleSameAs({
              formik,
              useSoldTo: false,
              useBillTo: false,
              usePayer: false
            });
        }}
        isActive={isDifferent}
        label={t('sameAsAddress.differentAddress')}
      />
    </Box>
  );
}

export default ToggleAddress;
